<!-- =========================================================================================
  File Name: App.vue
  Description: Main vue file - APP
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="app" :class="vueAppClasses">
    <router-view @setAppClasses="setAppClasses" :key="$route.fullPath" />
    <alert-dialog />
    <loading-dialog />

    <!-- 선택 시 나타나는 아이콘 -->
    <div
      v-if="showIcon"
      id="selection-icon"
      :style="{ top: iconPosition.top + 'px', left: iconPosition.left + 'px' }"
      @mousedown.prevent
      @click="onClickIcon"
    >
<!--      <feather-icon icon="LinkIcon" style="width: 16px; height: 16px;"/>-->
      <img :src="require('@/assets/icons/translation.png')" alt="번역아이콘" width="16" height="16"/>
    </div>

    <vs-popup id="TranslatePopup" :title="$t('번역')" :active.sync="showTranslate" >
      <div class="grid gap-2">
        <ml-textarea class="mb-0" readonly :value="selectText" :rows="3"/>
        <div class="w-full flex justify-between items-center">
          <vs-select class="flex-1" :disabled="isTranslate" autocomplete v-model="inputLang" @change="onTranslateClick">
            <vs-select-item v-for="item in locales" :value="item.lang" :text="item.name"/>
          </vs-select>

          <div class="flex justify-center" style="width: 32px;">
            <div v-if="isTranslate" class="loading" style="width:24px; height: 24px;">
              <div class="effect-1 effects"></div>
              <div class="effect-2 effects"></div>
              <div class="effect-3 effects"></div>
            </div>
            <feather-icon v-else icon="ArrowRightIcon" :width="16" :height="16"/>
          </div>

          <vs-select class="flex-1" :disabled="isTranslate" autocomplete v-model="outputLang" @change="onTranslateClick" :placeholder="$t('언어선택')">
            <vs-select-item v-for="item in locales" :value="item.lang" :text="item.name"/>
          </vs-select>
        </div>
        <ml-textarea readonly :value="translateText" :rows="3"/>
        <!-- TTS 버튼 또는 오디오 플레이어 영역 -->
        <div class="flex-shrink-0">
          <!-- 번역된 음성이 있는 경우 브라우저 기본 오디오 플레이어 표시 -->
          <div v-if="translateText && translatedAudioUrl" class="mb-4 w-full">
            <audio
              ref="audioPlayer"
              controls
              class="w-full"
              :src="audioBlobUrl || translatedAudioUrl"
              preload="metadata"
              @error="handleAudioError"
              @loadedmetadata="handleAudioLoaded"
            >
              브라우저가 오디오를 지원하지 않습니다.
            </audio>
          </div>

          <!-- 번역된 음성이 없고 텍스트만 있는 경우 TTS 버튼 표시 -->
          <div v-else-if="translateText && !translatedAudioUrl" class="mb-4 flex">
            <feather-icon
              icon="Volume2Icon"
              class="inline-block w-4 h-4 mr-1 cursor-pointer transition-colors duration-200"
              :style="ttsButtonStyle"
              @click="handleTTSClick"
              :title="ttsTooltip"
            />
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import themeConfig from "@/../themeConfig.js";
import AlertDialog from "@/components/AlertDialog.vue";
import LoadingDialog from "@/components/LoadingDialog.vue";

export default {
  components: {
    AlertDialog,
    LoadingDialog,
  },
  data() {
    return {
      vueAppClasses: [],
      showIcon: false,
      selectText: undefined,
      translateText: '',
      translatedAudioUrl: '',
      iconPosition: { top: 0, left: 0 },
      showTranslate: false,
      inputLang: 'ko-KR',
      outputLang: '',
      isTranslate: false,
      locales: [
        { name: '한국어', lang: 'ko-KR' },                             // Korean
        { name: 'বাংলা(방글라데시)', lang: 'bn-BD' },                  // Bengali
        { name: 'English(영어)', lang: 'en-US' },                     // English
        { name: 'Filipino(필리핀)', lang: 'fil-PH' },                 // Filipino
        { name: 'हिन्दी(인도)', lang: 'hi-IN' },                        // Hindi (※ 인도 추가)
        { name: 'Bahasa Indonesia(인도네시아어)', lang: 'id-ID' },     // Indonesian
        { name: '日本語(일본어)', lang: 'ja-JP' },                      // Japanese
        { name: 'ភាសាខ្មែរ(캄보디아)', lang: 'km-KH' },                // Khmer
        { name: 'Кыргызча(키르기스어)', lang: 'ky-KG' },               // Kyrgyz
        { name: 'ລາວ(라오스)', lang: 'lo-LA' },                        // Lao
        { name: 'Bahasa Melayu(말레이시아)', lang: 'ms-MY' },          // Malay
        { name: 'Монгол(몽골어)', lang: 'mn-MN' },                      // Mongolian
        { name: 'မြန်မာ(미얀마)', lang: 'my-MM' },                     // Burmese
        { name: 'नेपाली(네팔어)', lang: 'ne-NP' },                     // Nepali
        { name: 'Português(동티모르)', lang: 'pt-TL' },                // Portuguese (East Timor)
        { name: 'Русский(러시아)', lang: 'ru-UZ' },               // Russian (※ 우즈벡에서 자주 사용)
        { name: 'සිංහල(스리랑카)', lang: 'si-LK' },                    // Sinhala
        { name: 'தமிழ்(스리랑카)', lang: 'ta-LK' },                    // Tamil (Sri Lanka)
        { name: 'Türkçe(투르크멘어)', lang: 'tk-TM' },                  // Turkmen
        { name: 'Tiếng Việt(베트남어)', lang: 'vi-VN' },               // Vietnamese
        { name: 'Oʻzbekcha(우즈베크어)', lang: 'uz-UZ' },              // Uzbek
        { name: 'اردو(파키스탄)', lang: 'ur-PK' },                     // Urdu
        { name: '简体中文(중국어간체)', lang: 'zh-CN' },                 // Simplified Chinese
        { name: '繁體中文(중국어번체)', lang: 'zh-TW' },                 // Traditional Chinese
        { name: 'ไทย(태국어)', lang: 'th-TH' }                         // Thai
      ],
      audioBlobUrl: null, // 인증된 오디오 파일의 Blob URL

    };
  },
  computed: {
    myInfo () {
      return this.$store.state.auth.userInfo
    },

    // TTS 지원 여부
    isTTSSupported() {
      if (!window.speechSynthesis) return false;

      // 현재 언어에 대한 TTS 지원 여부 확인
      const voices = this.availableVoices;
      if (voices.length === 0) return false;

      // 현재 언어코드와 매칭되는 음성이 있는지 확인
      const currentLangCode = this.currentLang.split('-')[0]; // 'ko-KR' → 'ko'
      const hasMatchingVoice = voices.some(voice => {
        const voiceLang = voice.lang.split('-')[0];
        return voiceLang === currentLangCode;
      });

      return hasMatchingVoice;
    },
    // TTS 버튼 스타일
    ttsButtonStyle() {
      if (this.isTTSSupported) {
        return { color: '#7E72F2' }; // 지원되는 경우 보라색
      } else {
        return { color: '#9CA3AF' }; // 지원되지 않는 경우 회색
      }
    },
    // TTS 툴팁 텍스트
    ttsTooltip() {
      if (this.isTTSSupported) {
        return '텍스트를 음성으로 읽기';
      } else {
        return `${this.getCurrentLanguageName()} 언어는 음성 합성을 지원하지 않습니다`;
      }
    }
  },
  watch: {
    "$store.state.theme"(val) {
      this.toggleClassInBody(val);
    },
    "$vs.rtl"(val) {
      document.documentElement.setAttribute("dir", val ? "rtl" : "ltr");
    },
    myInfo(val) {
      if (val) {
        this.initRealtimeLocationTracking();
      }
    },
    showTranslate() {
      if (!this.showTranslate) {
        this.selectText = undefined;
        this.translateText = '';
        this.translatedAudioUrl = '';
        this.cleanupAudioBlob();
        this.isTranslate = false;
      }
    }
  },
  methods: {
    toggleClassInBody(className) {
      if (className === "dark") {
        if (document.body.className.match("theme-semi-dark")) document.body.classList.remove("theme-semi-dark");
        document.body.classList.add("theme-dark");
      } else if (className === "semi-dark") {
        if (document.body.className.match("theme-dark")) document.body.classList.remove("theme-dark");
        document.body.classList.add("theme-semi-dark");
      } else {
        if (document.body.className.match("theme-dark")) document.body.classList.remove("theme-dark");
        if (document.body.className.match("theme-semi-dark")) document.body.classList.remove("theme-semi-dark");
      }
    },
    setAppClasses(classesStr) {
      this.vueAppClasses.push(classesStr);
    },
    handleWindowResize() {
      this.$store.commit("UPDATE_WINDOW_WIDTH", window.innerWidth);

      // Set --vh property
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    },
    handleScroll() {
      this.$store.commit("UPDATE_WINDOW_SCROLL_Y", window.scrollY);
    },
    sendLocationToServer(lat, lng) {
      console.log("위치 정보 전송:", { latitude: lat, longitude: lng });
    },
    initRealtimeLocationTracking() {
      if (!navigator.geolocation) {
        console.error('이 브라우저는 위치 정보를 지원하지 않습니다.');
        return;
      }

      // 실시간 추적
      navigator.geolocation.watchPosition(
        position => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.sendLocationToServer(lat, lng);
        },
        error => {
          switch (error.code) {
          case error.PERMISSION_DENIED:
            console.error('사용자가 위치 정보 제공을 거부했습니다.');
            break;
          case error.POSITION_UNAVAILABLE:
            console.error('위치 정보를 사용할 수 없습니다.');
            break;
          case error.TIMEOUT:
            console.error('위치 정보 요청이 시간 초과되었습니다.');
            break;
          default:
            console.error('알 수 없는 오류 발생');
          }
        },
        {
          enableHighAccuracy: true, // GPS 정확도 우선
          maximumAge: 0,            // 캐시 사용 안 함
          timeout: 5000              // 응답 대기 시간
        }
      );
    },

    getCaretCoordinates(textarea, selectionEnd) {
      const div = document.createElement('div');
      const style = getComputedStyle(textarea);

      // textarea 스타일 복사
      div.style.whiteSpace = 'pre-wrap';
      div.style.wordWrap = 'break-word';
      div.style.position = 'absolute';
      div.style.visibility = 'hidden';
      div.style.font = style.font;
      div.style.padding = style.padding;
      div.style.border = style.border;
      div.style.overflow = 'auto';
      div.style.width = `${textarea.offsetWidth  }px`;

      // 선택된 텍스트까지 내용 넣기
      const text = textarea.value.substring(0, selectionEnd);
      div.textContent = text;

      document.body.appendChild(div);
      const span = document.createElement('span');
      span.textContent = '\u200b'; // zero-width space
      div.appendChild(span);

      const rect = span.getBoundingClientRect();
      document.body.removeChild(div);

      return { top: rect.top + window.scrollY, left: rect.left + window.scrollX };
    },
    handleSelection(event) {
      if (event && event.target.closest("#TranslatePopup")
        || event.target.id === "selection-icon"
        || event.target.tagName === "OPTION"
        || event.target.tagName === "BUTTON") return;

      const selection = window.getSelection();
      if (!selection) return;

      const text = selection.toString().trim();
      if (text.length > 0) {
        const activeElement = document.activeElement;

        if (!this.showTranslate && text) {
          this.selectText = text;
        }

        const range = selection.getRangeAt(0);
        let rect = range.getBoundingClientRect();
        if (activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'INPUT') {
          rect = activeElement.getBoundingClientRect();
        }

        let top = window.scrollY + rect.top - 20;  // 기본 위치 (위쪽)
        let left = window.scrollX + rect.right + 10; // 기본 위치 (오른쪽)

        const iconSize = 32; // 아이콘 크기(대략 px)
        const margin = 8;   // 화면 여백

        // 화면 위쪽으로 벗어나면 아래쪽으로
        if (top < window.scrollY + margin) {
          top = window.scrollY + rect.bottom + margin;
        }

        // 오른쪽 화면 끝 벗어나면 왼쪽으로
        if (left + iconSize > window.scrollX + window.innerWidth - margin) {
          left = window.scrollX + rect.left - iconSize - margin;
        }

        // 왼쪽 화면 끝 방지
        if (left < window.scrollX + margin) {
          left = window.scrollX + margin;
        }

        // 아래쪽 화면 끝 벗어나면 위쪽으로
        if (top + iconSize > window.scrollY + window.innerHeight - margin) {
          top = window.scrollY + rect.top - iconSize - margin;
        }

        this.iconPosition = { top, left };
        this.showIcon = true;
      } else {
        this.showIcon = false;
      }
    },
    onClickIcon() {
      this.showTranslate = true;
      this.showIcon = false;
      if (this.outputLang) {
        this.onTranslateClick();
      }
    },

    // 텍스트로 번역하기
    async onTranslateClick() {
      const text = this.selectText
      if (this.outputLang) {
        const response = await this.translateWithText(text);
        this.cleanupAudioBlob();
        this.translateText = response.translatedText;
        this.translatedAudioUrl = response.audioUrl;
        // 번역된 음성 자동 재생
        if (response.audioUrl) {
          await this.playTranslatedAudio();
        }
      }
    },
    async translateWithText(text, voice = 'nova') {
      if (this.isTranslate) return;
      this.isTranslate = true;
      try {
        // 입력 검증
        if (!text || text.trim() === '') {
          throw new Error(this.$t('번역할 텍스트를 입력해주세요.'));
        }

        // 번역 방향에 따라 입력/출력 언어 결정
        const inputLang = this.inputLang.split('-')[0]
        const outputLang = this.outputLang.split('-')[0]

        // API 호출
        const response = await this.$store.dispatch('miras/VOICE_TRANSLATE', {
          text: text.trim(),
          inputLang,
          outputLang,
          voice
        });

        this.isTranslate = false;
        return response
      } catch (error) {
        console.error(this.$t('텍스트 번역 실패:'), error);
        this.$vs.notify({
          title: this.$t('번역 오류'),
          text: error.message,
          color: 'danger'
        });
        throw error;
      } finally {
        this.isTranslating = false;
      }
    },


    // 번역된 음성 재생하기 - 브라우저 플레이어 사용
    async playTranslatedAudio() {
      try {
        if (!this.translatedAudioUrl) {
          const error = new Error('재생할 음성 파일이 없습니다.');
          this.$vs.notify({
            title: '재생 불가',
            text: error.message,
            color: 'warning'
          });
          throw error;
        }

        // DOM 업데이트만 대기 (고정 시간 대기 제거)
        console.log('DOM 업데이트 대기 중...');
        await this.$nextTick();

        try {
          // 전체 자동 재생에도 타임아웃 적용 (45초로 증가)
          await Promise.race([
            this.playWithBrowserPlayer(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('전체 재생 타임아웃')), 45000))
          ]);
          console.log('브라우저 플레이어 자동 재생 완료');
        } catch (playerError) {
          console.warn('브라우저 플레이어 재생 실패, 백그라운드로 대체:', playerError.message);
          // 재생 실패 시 fallback으로 백그라운드 재생 사용하지 않음 (사용자가 수동으로 재생할 수 있음)
          console.log('사용자가 수동으로 플레이어를 조작할 수 있습니다.');
        }

      } catch (error) {
        console.error('음성 재생 실패:', error);
        this.$vs.notify({
          title: '재생 오류',
          text: error.message || '음성 파일을 재생할 수 없습니다.',
          color: 'danger'
        });
        throw error;
      }
    },


    // 브라우저 플레이어를 통한 자동 재생
    async playWithBrowserPlayer() {
      try {
        console.log('브라우저 플레이어 재생 시도 시작');

        // DOM 업데이트 대기
        await this.$nextTick();

        if (!this.$refs.audioPlayer) {
          console.error('오디오 플레이어 ref를 찾을 수 없음');
          throw new Error('오디오 플레이어를 찾을 수 없습니다.');
        }

        // Authorization이 필요한 URL인 경우 인증된 오디오 로딩 먼저 확인
        if (this.translatedAudioUrl && this.translatedAudioUrl.includes('/api/v1/file')) {
          console.log('인증이 필요한 오디오 URL 감지, 인증된 로딩 확인 중...');

          // 이미 로딩 중이면 완료까지 대기
          if (this.isLoadingAudio) {
            console.log('이미 오디오 로딩 중, 완료까지 대기...');
            await Promise.race([
              this.audioLoadingPromise || Promise.resolve(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('로딩 대기 타임아웃')), 15000))
            ]);
          }

          // audioBlobUrl이 없는 경우에만 새로 로드
          if (!this.audioBlobUrl) {
            console.log('인증된 오디오 Blob URL이 없음, 새로 로드 시작');
            await Promise.race([
              this.loadAuthenticatedAudio(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('인증된 오디오 로드 타임아웃')), 15000))
            ]);
          } else {
            console.log('인증된 오디오 Blob URL 이미 존재:', this.audioBlobUrl);
          }

          // 로딩 후에도 audioBlobUrl이 없으면 에러
          if (!this.audioBlobUrl) {
            throw new Error('인증된 오디오 로딩에 실패했습니다.');
          }

          console.log('인증된 오디오 Blob URL 준비 완료:', this.audioBlobUrl);
        }

        const audioElement = this.$refs.audioPlayer;
        console.log('오디오 엘리먼트 찾음, 현재 src:', audioElement.src);

        // 오디오가 재생 준비될 때까지 대기 (타임아웃 포함)
        console.log('오디오 재생 준비 대기 중...');
        await Promise.race([
          this.waitForAudioReady(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('오디오 준비 타임아웃')), 15000))
        ]);

        // 재생 시작
        console.log('브라우저 플레이어에서 재생 시작');
        audioElement.currentTime = 0; // 처음부터 재생

        const playPromise = audioElement.play();

        if (playPromise !== undefined) {
          await Promise.race([
            playPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('재생 시작 타임아웃')), 5000))
          ]);
          console.log('브라우저 플레이어 자동 재생 시작됨');
        }

        // 재생 완료까지 대기 (최대 30초)
        return new Promise((resolve, reject) => {
          let isResolved = false;

          const cleanup = () => {
            audioElement.removeEventListener('ended', handleEnded);
            audioElement.removeEventListener('error', handleError);
            if (timeoutId) clearTimeout(timeoutId);
          };

          const handleEnded = () => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.log('브라우저 플레이어 재생 완료');
            resolve();
          };

          const handleError = (error) => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.error('브라우저 플레이어 재생 중 오류:', error);
            reject(error);
          };

          audioElement.addEventListener('ended', handleEnded);
          audioElement.addEventListener('error', handleError);

          // 30초 타임아웃
          const timeoutId = setTimeout(() => {
            if (isResolved) return;
            isResolved = true;
            cleanup();
            console.warn('브라우저 플레이어 재생 타임아웃');
            resolve(); // 타임아웃이어도 성공으로 처리
          }, 30000);
        });

      } catch (error) {
        console.error('브라우저 플레이어 재생 실패:', error);
        throw error;
      }
    },

    // 오디오 에러 이벤트
    handleAudioError(event) {
      const audioElement = event.target;
      const errorCode = (audioElement.error && audioElement.error.code);
      const errorMessage = (audioElement.error && audioElement.error.message);

      console.error('오디오 로드/재생 오류:', {
        event,
        errorCode,
        errorMessage,
        src: audioElement.src,
        currentSrc: audioElement.currentSrc,
        readyState: audioElement.readyState,
        networkState: audioElement.networkState
      });

      // 에러 코드별 상세 로깅
      switch (errorCode) {
      case 1: // MEDIA_ERR_ABORTED
        console.error('오디오 로드가 중단됨');
        break;
      case 2: // MEDIA_ERR_NETWORK
        console.error('네트워크 오류로 오디오 로드 실패');
        break;
      case 3: // MEDIA_ERR_DECODE
        console.error('오디오 디코딩 오류');
        break;
      case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
        console.error('지원되지 않는 오디오 형식 또는 소스');
        break;
      default:
        console.error('알 수 없는 오디오 오류');
      }

      // 인증된 URL로 재시도 (단, 이미 재시도 중이 아닌 경우만)
      if (!this.audioBlobUrl && this.translatedAudioUrl && this.translatedAudioUrl.includes('/api/v1/file') && !this.isLoadingAudio) {
        console.log('인증된 오디오 로드 재시도 (에러 핸들러)');
        this.loadAuthenticatedAudio().catch(retryError => {
          console.error('에러 핸들러 재시도도 실패:', retryError);
        });
      } else if (this.isLoadingAudio) {
        console.log('이미 오디오 로딩 중이므로 재시도 건너뛰기');
      } else if (this.audioBlobUrl) {
        console.log('이미 Blob URL 존재하므로 재시도 불필요');
      }
    },

    // 오디오 로드 완료 이벤트
    handleAudioLoaded() {
      console.log('오디오 메타데이터 로드 완료');
    },


    // TTS 버튼 클릭 핸들러
    handleTTSClick() {
      if (this.isTTSSupported) {
        this.playTextToSpeech();
      } else {
        // 지원되지 않는 언어인 경우 알림 표시
        const languageName = this.getCurrentLanguageName();
        const message = `${languageName} 언어는 현재 브라우저에서 음성 합성을 지원하지 않습니다.`;

        if (this.$vs) {
          this.$vs.notify({
            title: 'TTS 지원 안됨',
            text: message,
            color: 'warning',
            icon: 'volume_off'
          });
        } else {
          alert(message);
        }
      }
    },


    // 브라우저 내장 TTS로 텍스트 읽기
    playTextToSpeech() {
      // 기존에 재생 중인 음성이 있으면 중단
      window.speechSynthesis.cancel();

      const msg = new SpeechSynthesisUtterance();
      msg.text = this.localText;
      msg.lang = this.currentLang;

      msg.onend = () => {
        console.log('TTS 재생 완료');
      };

      msg.onerror = (error) => {
        console.error('TTS 재생 실패:', error);
      };

      window.speechSynthesis.speak(msg);
    },

    // 인증된 오디오 파일 로드 (중복 방지)
    async loadAuthenticatedAudio() {
      try {
        if (!this.translatedAudioUrl) {
          console.warn('번역된 오디오 URL이 없습니다.');
          return;
        }

        // 이미 로딩 중이면 기존 Promise 반환 (중복 방지)
        if (this.isLoadingAudio && this.audioLoadingPromise) {
          console.log('이미 오디오 로딩 중, 기존 Promise 사용');
          return await this.audioLoadingPromise;
        }

        console.log('인증된 오디오 로드 시작:', this.translatedAudioUrl);

        // 로딩 상태 설정
        this.isLoadingAudio = true;

        // 새로운 로딩 Promise 생성
        this.audioLoadingPromise = this._doLoadAuthenticatedAudio();

        const result = await this.audioLoadingPromise;

        return result;
      } catch (error) {
        console.error('인증된 오디오 로드 실패:', error);
        throw error;
      } finally {
        // 로딩 상태 해제
        this.isLoadingAudio = false;
        this.audioLoadingPromise = null;
      }
    },

    // 실제 로딩 작업 수행
    async _doLoadAuthenticatedAudio() {
      // Authorization이 필요한 URL인지 확인
      if (this.translatedAudioUrl.includes('/api/v1/file')) {
        console.log('Authorization 헤더가 필요한 URL로 인증된 요청 시작');

        // HttpRequest를 사용하여 Authorization 헤더가 포함된 요청
        const response = await Promise.race([
          this.$http.get(this.translatedAudioUrl, {
            responseType: 'blob',
            timeout: 30000  // 30초 타임아웃 명시적 설정
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('HTTP 요청 타임아웃')), 30000))
        ]);

        if (response && response.data && response.data.size > 0) {
          // 기존 Blob URL 정리
          this.cleanupAudioBlob();

          // 새로운 Blob URL 생성
          const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
          this.audioBlobUrl = URL.createObjectURL(audioBlob);

          console.log('인증된 오디오 Blob URL 생성 완료:', this.audioBlobUrl, '크기:', audioBlob.size, 'bytes');

          // DOM 업데이트 대기
          await this.$nextTick();

          // 오디오 엘리먼트가 새 소스를 완전히 로드할 수 있도록 추가 대기
          await new Promise(resolve => setTimeout(resolve, 100));

          console.log('인증된 오디오 로딩 완료');
        } else {
          throw new Error('오디오 데이터를 받을 수 없거나 데이터가 비어있습니다.');
        }
      } else {
        // 일반 URL인 경우 직접 사용
        console.log('일반 URL로 직접 사용');
        this.audioBlobUrl = null;

        // DOM 업데이트 대기
        await this.$nextTick();
      }
    },

    // 오디오 Blob URL 정리
    cleanupAudioBlob() {
      if (this.audioBlobUrl) {
        console.log('오디오 Blob URL 정리:', this.audioBlobUrl);
        URL.revokeObjectURL(this.audioBlobUrl);
        this.audioBlobUrl = null;
      }

      // 로딩 상태도 초기화
      this.isLoadingAudio = false;
      this.audioLoadingPromise = null;
    },


    // 오디오 엘리먼트가 재생 준비될 때까지 대기
    async waitForAudioReady() {
      return new Promise((resolve, reject) => {
        const audioElement = this.$refs.audioPlayer;
        if (!audioElement) {
          reject(new Error('오디오 엘리먼트를 찾을 수 없습니다.'));
          return;
        }

        // 현재 src 확인
        const currentSrc = audioElement.src || audioElement.currentSrc;
        console.log('오디오 readyState 확인 - src:', currentSrc, 'readyState:', audioElement.readyState);

        // src가 없으면 에러
        if (!currentSrc || currentSrc === '') {
          reject(new Error('오디오 src가 설정되지 않았습니다.'));
          return;
        }

        // 이미 준비된 경우 즉시 resolve
        if (audioElement.readyState >= 3) { // HAVE_FUTURE_DATA 이상
          console.log('오디오가 이미 준비됨 (readyState:', audioElement.readyState, ')');
          resolve();
          return;
        }

        console.log('오디오 준비 대기 중... (현재 readyState:', audioElement.readyState, ', src:', currentSrc, ')');

        let timeoutId;
        let isResolved = false;
        let checkIntervalId;

        const cleanup = () => {
          if (timeoutId) clearTimeout(timeoutId);
          if (checkIntervalId) clearInterval(checkIntervalId);
          audioElement.removeEventListener('canplay', handleCanPlay);
          audioElement.removeEventListener('canplaythrough', handleCanPlay);
          audioElement.removeEventListener('loadeddata', handleCanPlay);
          audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audioElement.removeEventListener('error', handleError);
        };

        const handleCanPlay = () => {
          if (isResolved) return;
          isResolved = true;
          console.log('오디오 재생 준비 완료 (readyState:', audioElement.readyState, ')');
          cleanup();
          resolve();
        };

        const handleLoadedMetadata = () => {
          console.log('오디오 메타데이터 로드 완료 (readyState:', audioElement.readyState, ')');
          // 메타데이터만으로는 재생 준비가 완료된 것은 아니므로 계속 대기
        };

        const handleError = (error) => {
          if (isResolved) return;
          isResolved = true;
          console.error('오디오 로드 중 에러:', error, 'error code:', (audioElement.error && audioElement.error.code), 'error message:', audioElement.error && audioElement.error.message);
          cleanup();
          reject(new Error(`오디오 로드 에러: ${(audioElement.error && audioElement.error.message) || 'Unknown error'}`));
        };

        // 이벤트 리스너 등록
        audioElement.addEventListener('canplay', handleCanPlay);
        audioElement.addEventListener('canplaythrough', handleCanPlay);
        audioElement.addEventListener('loadeddata', handleCanPlay);
        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        audioElement.addEventListener('error', handleError);

        // readyState 주기적 체크 (디버깅용)
        checkIntervalId = setInterval(() => {
          if (isResolved) return;
          console.log('오디오 readyState 체크:', audioElement.readyState, '네트워크 상태:', audioElement.networkState);
        }, 1000);

        // 타임아웃 설정 (10초로 증가)
        timeoutId = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          console.warn('오디오 로드 타임아웃, 현재 상태로 진행 (readyState:', audioElement.readyState, ', networkState:', audioElement.networkState, ')');

          if (audioElement.readyState >= 2) { // HAVE_CURRENT_DATA 이상이면 시도
            console.log('최소 필요한 데이터가 로드됨, 재생 시도');
            resolve();
          } else if (audioElement.readyState >= 1) { // 최소 메타데이터라도 로드됐으면 진행
            console.log('메타데이터만 로드됨, 재생 시도');
            resolve();
          } else {
            reject(new Error(`오디오 로드 타임아웃 (readyState: ${audioElement.readyState}, networkState: ${audioElement.networkState})`));
          }
        }, 10000);

        // 강제로 로드 시도 (필요한 경우)
        if (audioElement.readyState === 0) {
          console.log('readyState가 0이므로 강제 로드 시도');
          try {
            audioElement.load();
          } catch (loadError) {
            console.warn('강제 로드 실패:', loadError);
          }
        }
      });
    },
  },
  mounted() {
    this.toggleClassInBody(themeConfig.theme);
    this.$store.commit("UPDATE_WINDOW_WIDTH", window.innerWidth);

    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty("--vh", `${vh}px`);

    document.addEventListener("mouseup", this.handleSelection);
  },
  beforeDestroy () {
    document.removeEventListener("mouseup", this.handleSelection);
  },
  async created() {
    const dir = this.$vs.rtl ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);

    window.addEventListener("resize", this.handleWindowResize);
    window.addEventListener("scroll", this.handleScroll);

    // i18n 초기설정
    this.$i18n.locale = localStorage.getItem("locale") || "ko_KR"; // 기본 언어 설정
  },
  destroyed() {
    window.removeEventListener("resize", this.handleWindowResize);
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style>
.vs-textarea-primary textarea {
  resize: vertical !important;
}

.vs-textarea-primary.resize-none textarea {
  resize: none !important;
}

.vs-popup {
  position: fixed;
}
</style>

<style>
#selection-icon {
  position: absolute;
  cursor: pointer;
  font-size: 18px;
  color: white;
  background: #758af5;
  border: 1px solid #ddd;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  z-index: 999999999;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
}
</style>

<style lang="scss" scoped>
.rotate-icon {
  display: inline-block;
  font-size: 16px;
  transform-origin: center center;
  animation: spin 2s linear infinite; /* 이름, 속도, 선형, 반복 */
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
